'use client';

import * as React from "react"
import { cn } from "@shared/utils"
import { withPlatformComponents, WithPlatformComponents } from '../../platform'

/**
 * `Card` is a layout container with a border, shadow, and background,
 * used to group content in a consistent and styled box.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description</CardDescription>
 *   </CardHeader>
 *   <CardContent>Content goes here</CardContent>
 *   <CardFooter>Footer actions</CardFooter>
 * </Card>
 * ```
 * @internal
 */
interface CardProps {
  className?: string;
  children: React.ReactNode;
}

const CardComponent: React.FC<CardProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { View } = components;
  
  return (
    <View
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm",
        className
      )}
    >
      {children}
    </View>
  );
};

const Card = withPlatformComponents(CardComponent);

/**
 * `CardHeader` wraps the top section of a Card, typically used for titles and descriptions.
 * @internal
 */
interface CardHeaderProps {
  className?: string;
  children: React.ReactNode;
}

const CardHeaderComponent: React.FC<CardHeaderProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { View } = components;
  
  return (
    <View className={cn("flex flex-col space-y-1.5 p-6", className)}>
      {children}
    </View>
  );
};

const CardHeader = withPlatformComponents(CardHeaderComponent);

/**
 * `CardTitle` displays the title of the Card.
 * @internal
 */
interface CardTitleProps {
  className?: string;
  children: React.ReactNode;
}

const CardTitleComponent: React.FC<CardTitleProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { Text } = components;
  
  return (
    <Text
      className={cn(
        "text-2xl font-semibold leading-none tracking-tight",
        className
      )}
    >
      {children}
    </Text>
  );
};

const CardTitle = withPlatformComponents(CardTitleComponent);

/**
 * `CardDescription` is typically placed below the title to add context or additional details.
 * @internal
 */
interface CardDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

const CardDescriptionComponent: React.FC<CardDescriptionProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { Text } = components;
  
  return (
    <Text className={cn("text-sm text-muted-foreground", className)}>
      {children}
    </Text>
  );
};

const CardDescription = withPlatformComponents(CardDescriptionComponent);

/**
 * `CardContent` is the main content area of the Card.
 * @internal
 */
interface CardContentProps {
  className?: string;
  children: React.ReactNode;
}

const CardContentComponent: React.FC<CardContentProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { View } = components;
  
  return (
    <View className={cn("p-6 pt-0", className)}>
      {children}
    </View>
  );
};

const CardContent = withPlatformComponents(CardContentComponent);

/**
 * `CardFooter` is typically used for actions at the bottom of the Card.
 * @internal
 */
interface CardFooterProps {
  className?: string;
  children: React.ReactNode;
}

const CardFooterComponent: React.FC<CardFooterProps & WithPlatformComponents> = ({ 
  className, 
  children,
  components 
}) => {
  const { View } = components;
  
  return (
    <View className={cn("flex items-center p-6 pt-0", className)}>
      {children}
    </View>
  );
};

const CardFooter = withPlatformComponents(CardFooterComponent);

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent
}
